import React, { useCallback } from 'react'
import styled, { css } from 'styled-components'
import { getIconDefinition } from './constants'
import { InlineSvg } from './inline-svg'
import { IconProps, IconSize } from './types'
import { useTheme } from '~common/theme/theme-provider'
import type { Theme } from '~common/theme/types'

const StyledInlineSvg = styled(InlineSvg)<{ $color: string }>`
    color: ${({ $color }) => $color};
`

const SIZE_MAP: Record<IconSize, string> = {
    small: '18px',
    medium: '22px',
    large: '30px',
    sm: '20px',
}

/**
 * Resolves a theme color path to an actual color value
 * Supports paths like "greyScale[500]" or "text.primary"
 */
function resolveThemeColor(theme: Theme, colorPath: string): string {
    if (!colorPath || colorPath === 'transparent') {
        return 'transparent'
    }

    try {
        // Handle direct color values (hex codes, etc.)
        if (colorPath.startsWith('#') || colorPath.startsWith('rgb')) {
            return colorPath
        }

        let current: unknown = theme.colors

        // Split by dots, but handle array notation like [500]
        const parts = colorPath.split('.').flatMap((part) => {
            // Check if part contains array notation like "greyScale[500]"
            const arrayMatch = part.match(/^(.+)\[(\d+)\]$/)
            if (arrayMatch) {
                return [arrayMatch[1], `[${arrayMatch[2]}]`]
            }
            return part
        })

        for (const part of parts) {
            if (part.startsWith('[') && part.endsWith(']')) {
                // Array access like [500]
                const index = parseInt(part.slice(1, -1), 10)
                current = (current as Record<number, unknown>)[index]
            } else {
                // Object property access
                current = (current as Record<string, unknown>)[part]
            }

            if (current === undefined) {
                console.warn(
                    `Theme color path "${colorPath}" not found, falling back to primary text color`,
                )
                return theme.colors.text.primary
            }
        }

        return current as string
    } catch (error) {
        console.warn(`Error resolving theme color path "${colorPath}":`, error)
        return theme.colors.text.primary
    }
}

/**
 * Resolves a theme spacing path to an actual spacing value
 * Supports paths like "md" or "spacing.xl"
 */
function resolveThemeSpacing(theme: Theme, spacingPath: string): string {
    if (!spacingPath) {
        return '0'
    }

    try {
        // Handle direct spacing values (e.g., '8px', '1rem')
        if (/^\d+(px|rem|em|%|vh|vw)$/i.test(spacingPath)) {
            return spacingPath
        }

        let current: unknown = theme.spacing

        // Split by dots, but handle array notation if needed (though spacing is usually object-based)
        const parts = spacingPath.split('.').flatMap((part) => {
            // For now, assume no array notation in spacing, but keep similar logic
            const arrayMatch = part.match(/^(.+)\[(\d+)\]$/)
            if (arrayMatch) {
                return [arrayMatch[1], `[${arrayMatch[2]}]`]
            }
            return part
        })

        for (const part of parts) {
            if (part.startsWith('[') && part.endsWith(']')) {
                // Array access
                const index = parseInt(part.slice(1, -1), 10)
                current = (current as Record<number, unknown>)[index]
            } else {
                // Object property access
                current = (current as Record<string, unknown>)[part]
            }

            if (current === undefined) {
                console.warn(
                    `Theme spacing path "${spacingPath}" not found, falling back to 0`,
                )
                return '0'
            }
        }

        return current as string
    } catch (error) {
        console.warn(
            `Error resolving theme spacing path "${spacingPath}":`,
            error,
        )
        return '0'
    }
}

export function Icon({
    name,
    height,
    width,
    size = 'medium',
    iconColor = ({ theme }: { theme: Theme }) => theme.colors.text.primary,
    onClick,
    hover = false,
    className,
    style,
    backgroundColor = 'transparent',
    padding,
    alt,
}: IconProps) {
    const { theme } = useTheme()
    const iconDefinition = getIconDefinition(name)

    if (!iconDefinition) {
        console.warn(`Icon "${name}" not found in icon dictionary`)
        return null
    }

    // Determine the final size (predefined or custom)
    let finalSize = SIZE_MAP[size as IconSize] || size

    if (height && width) {
        finalSize = `${height}px`
    }

    // Handle clickability
    const isClickable = !!onClick

    // Common props for both SVG and IMG
    const commonProps = {
        className,
        style: {
            width: 'auto',
            height: finalSize,
            ...style,
        },
        onClick,
        role: isClickable ? 'button' : undefined,
        tabIndex: isClickable ? 0 : undefined,
        onKeyDown: isClickable
            ? (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onClick?.(e as any)
                  }
              }
            : undefined,
    }

    const resolveIconPath = useCallback((path: string) => {
        if (typeof window !== 'undefined') {
            const win = window as Window & {
                chrome?: {
                    runtime?: {
                        getURL?: (path: string) => string
                    }
                }
            }
            if (win.chrome?.runtime?.getURL) {
                return win.chrome.runtime.getURL(path)
            }
        }
        return path
    }, [])

    // Render SVG using HTTP URL from constants
    if (iconDefinition.format === 'svg') {
        const svgUrl = resolveIconPath(iconDefinition.path)

        if (!svgUrl) {
            console.warn(`SVG path not found for icon "${name}"`)
            return null
        }

        // Determine fill color based on theme and iconColor
        const getSvgFillColor = () => {
            // If iconColor is a function, call it with theme context
            if (typeof iconColor === 'function') {
                return iconColor({ theme: theme as Theme })
            }
            // Otherwise resolve string paths
            return resolveThemeColor(theme as Theme, iconColor)
        }

        return (
            <IconContainer
                $hover={hover}
                $isClickable={isClickable}
                $size={finalSize}
                $backgroundColor={backgroundColor}
                $padding={padding}
                {...commonProps}
            >
                {typeof window === 'undefined' ? (
                    <span
                        style={{
                            width: finalSize,
                            height: finalSize,
                            display: 'inline-block',
                        }}
                    />
                ) : (
                    <StyledInlineSvg
                        src={svgUrl}
                        $color={getSvgFillColor()}
                        alt={alt || iconDefinition.alt}
                        size={finalSize}
                        colorType={iconDefinition.colorType}
                    />
                )}
            </IconContainer>
        )
    }

    // Render PNG/JPG/GIF using IMG element
    const imgSrc = resolveIconPath(iconDefinition.path)
    return (
        <IconContainer
            $hover={hover}
            $isClickable={isClickable}
            $size={finalSize}
            $backgroundColor={backgroundColor}
            $padding={padding}
            {...commonProps}
        >
            <StyledImg
                src={imgSrc}
                alt={alt || iconDefinition.alt}
                $size={finalSize}
            />
        </IconContainer>
    )
}

// Styled Components
interface IconContainerProps {
    $hover: boolean
    $isClickable: boolean
    $size: string
    $backgroundColor: string
    $padding?: string
}

const IconContainer = styled.div<IconContainerProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: ${({ theme }) => (theme as Theme).borderRadius.lg};
    transition: all ${({ theme }) => (theme as Theme).transitions.fast};

    ${({ $hover, $isClickable }) => {
        if ($hover && $isClickable) {
            return css`
                &:hover {
                    background-color: ${({ theme }) =>
                        (theme as Theme).colors.greyScale[200]};
                    cursor: pointer;
                }

                &:focus {
                    outline: 2px solid
                        ${({ theme }) => (theme as Theme).colors.border.focus};
                    outline-offset: 2px;
                }
            `
        }

        if ($isClickable) {
            return css`
                cursor: pointer;

                &:focus {
                    outline: 2px solid
                        ${({ theme }) => (theme as Theme).colors.border.focus};
                    outline-offset: 2px;
                }
            `
        }

        return css``
    }}

    ${({ $backgroundColor }) => {
        if ($backgroundColor) {
            return css`
                background-color: ${({ theme }) =>
                    resolveThemeColor(theme as Theme, $backgroundColor)};
            `
        }
    }}

    ${({ $padding }) => {
        if ($padding) {
            return css`
                padding: ${({ theme }) =>
                    resolveThemeSpacing(theme as Theme, $padding)};
            `
        }
    }}
`

interface StyledImgProps {
    $size: string
}

const StyledImg = styled.img<StyledImgProps>`
    width: auto;
    height: ${({ $size }) => $size};
    object-fit: contain;
    pointer-events: none;
`
